// src/components/AboutB2Bhaart.tsx
import React from 'react';
import { ShieldCheck, TrendingUp, Truck, Users } from 'lucide-react'; // Using Lucide Icons for better look

const FeatureVisual: React.FC<{ icon: React.ReactNode }> = ({ icon }) => (
    <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-teal-50 text-teal-600 mb-6 flex-shrink-0 border border-teal-100 shadow-sm group-hover:bg-teal-600 group-hover:text-white group-hover:scale-110 transition-all duration-300">
        {icon}
    </div>
);

const AboutTradyFrog: React.FC = () => {
    const coreValues = [
        {
            title: "Verified Supplier Network",
            description: "A strict multi-point verification process ensures every seller is genuine and reliable. Zero sourcing risk for high-volume procurement.",
            icon: <ShieldCheck className="w-8 h-8" />
        },
        {
            title: "Direct Sourcing, No Commission",
            description: "Connect directly with manufacturers and wholesalers. We eliminate middlemen to ensure you get the most competitive factory prices.",
            icon: <Users className="w-8 h-8" />
        },
        {
            title: "Scalable Industrial Procurement",
            description: "Engineered for high-volume orders. Dedicated support for bulk RFQs, custom logistics, and GST-compliant invoicing.",
            icon: <TrendingUp className="w-8 h-8" />
        },
        {
            title: "End-to-End Transparency",
            description: "Real-time order tracking, secure payment gateways, and a transparent seller rating system for complete peace of mind.",
            icon: <Truck className="w-8 h-8" />
        }
    ];

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "B2B Marketplace",
        "provider": {
            "@type": "Organization",
            "name": "Trady Frog",
            "url": "https://tradyfrog.com"
        },
        "areaServed": "India",
        "description": "Verified B2B marketplace for industrial procurement and bulk sourcing."
    };

    return (
        <section className="font-sans bg-gradient-to-b from-white to-gray-50 py-20 border-t border-gray-100">
            <script type="application/ld+json">
                {JSON.stringify(schemaData)}
            </script>
            
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* Header Section */}
                <header className="text-center mb-16 max-w-4xl mx-auto">
                    <span className="inline-block py-1 px-3 rounded-full bg-teal-50 text-teal-700 text-xs font-bold tracking-widest uppercase mb-4 border border-teal-100">
                        Why Choose TradyFrog ?
                    </span>
                    
                    <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight tracking-tight font-sans">
                        Revolutionizing <span className="text-teal-600 relative">B2B Commerce</span> for <br className="hidden md:block"/> Modern India
                    </h1>
                    
                    <p className="text-lg md:text-xl text-gray-500 leading-relaxed font-medium">
                        We bridge the gap between industrial buyers and verified suppliers with technology-driven efficiency, transparency, and trust.
                    </p>
                </header>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {coreValues.map((value, index) => (
                        <article 
                            key={index} 
                            className="group bg-white p-8 rounded-3xl shadow-sm hover:shadow-2xl border border-gray-100 transition-all duration-300 transform hover:-translate-y-2 flex flex-col items-start"
                        >
                            <FeatureVisual icon={value.icon} />
                            
                            <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-700 transition-colors">
                                {value.title}
                            </h3>
                            
                            <p className="text-gray-600 text-sm leading-relaxed">
                                {value.description}
                            </p>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AboutTradyFrog;